import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TechnicalCriteriaType } from '@1po/1po-bff-fe-spec/generated/catalog/vehicle/model/TechnicalCriteriaType';
import {
  getLastIAMTechnicalCriteria,
  getLastTechnicalCriteria,
  getLastVehicleDetail,
} from 'domains/catalog/Catalog.store';
import { joinStrings } from 'pages/CatalogPage/IAM/VehicleTechnicalDetails/TechnicalCriteria';
import { getData } from 'utils';

type TableDataType = { title: string; value: string }[];

const codesDh: [TechnicalCriteriaType, string][] = [
  ['CONFIGURATION', '336'], // brand designation
  ['CONFIGURATION', '007'], // family
  ['BODY_TYPE', 'E86'], // main body color
  ['BODY_TYPE', 'E88'], // secondary body color
  ['CONFIGURATION', 'APVDT1'], // vehicle manufacturer date
  ['CONFIGURATION', '008'], // bodywork type
  ['MAIN_ENGINE', '019'], // type of fuel
  ['MAIN_ENGINE', '803'], // power in DIN HP / KW
  ['MAIN_ENGINE', '999'], // engine type
  ['MAIN_ENGINE', '802'], // engine capacity
  ['MAIN_ENGINE', '998'], // engine suffix
  ['TRANSMISSION', '036'], // transmission type
  ['TRANSMISSION', '427'], // gearbox type/number of gears
  ['TRANSMISSION', '996'], // gearbox suffix
  ['CONFIGURATION', 'code_nrce'], // community reception code
  ['CONFIGURATION', 'code_tapv'], // TAPV
];

export const useGetTechnicalPdfDataDh = (): TableDataType => {
  const { t } = useTranslation();
  const technicalDataDh = getData(useSelector(getLastTechnicalCriteria));
  const vehicleData = useSelector(getLastVehicleDetail);
  const vehicleDataItems = [
    { title: t('catalog.details.technical_criteria.version.vin', 'VIN'), value: vehicleData?.vin },
    {
      title: t('catalog.details.technical_criteria.registration_number', 'Registration number'),
      value: vehicleData?.vrn,
    },
  ].filter((item) => item.value) as TableDataType;
  const vehicleDataByCodesItems = codesDh
    .map((item) => {
      const [category, code] = item;
      const techData = technicalDataDh?.groups.get(category)?.find((data) => data.code === code);
      return {
        title: techData?.title,
        value: techData?.value,
      };
    })
    .filter((item) => item.title && item.value) as TableDataType;
  return [...vehicleDataItems, ...vehicleDataByCodesItems];
};

export const useGetTechnicalPdfDataIam = (): TableDataType => {
  const { t } = useTranslation();
  const technicalDataIam = getData(useSelector(getLastIAMTechnicalCriteria));
  const vehicleData = useSelector(getLastVehicleDetail);
  const techCrit = getData(technicalDataIam);
  return [
    {
      title: t('catalog.details.technical_criteria.version.vin', 'VIN'),
      value: techCrit?.version.vin,
    },
    {
      title: t('catalog.details.technical_criteria.registration_number', 'Registration number'),
      value: vehicleData?.vrn,
    },
    {
      title: t('catalog.details.technical_criteria.version.brand', 'Brand'),
      value: techCrit?.version.brand,
    },
    {
      title: t('catalog.details.technical_criteria.version.family', 'Family'),
      value: techCrit?.version.family,
    },
    {
      title: t('catalog.details.technical_criteria.version.version', 'Version'),
      value: techCrit?.version.version,
    },
    {
      title: t('catalog.details.technical_criteria.version.color', 'Color'),
      value: techCrit?.version.color,
    },
    {
      title: t('catalog.details.technical_criteria.version.firstRegistrationDate', 'First registration date'),
      value: techCrit?.version.firstRegistrationDate,
    },
    {
      title: t('catalog.details.technical_criteria.version.cnit_tvv', 'CNIT / TVV'),
      value: techCrit?.version.cnit && techCrit?.version.tvv && `${techCrit?.version.cnit} / ${techCrit?.version.tvv}`,
    },
    {
      title: t('catalog.details.technical_criteria.version.body_type', 'Body type'),
      value:
        techCrit?.version.doors &&
        `${techCrit?.version.bodyType} ${t('catalog.parts.banner.doors', '{{doors}} doors', {
          doors: techCrit?.version.doors,
        })}`,
    },
    {
      title: t('catalog.details.technical_criteria.version.seats', 'Seats'),
      value: techCrit?.version.seats,
    },
    {
      title: t('catalog.details.technical_criteria.main_engine.energy', 'Energy'),
      value: joinStrings(techCrit?.mainEngine.energy),
    },
    {
      title: t('catalog.details.technical_criteria.main_engine.engine_type', 'Engine type'),
      value: joinStrings(techCrit?.mainEngine.engineType),
    },
    {
      title: t('catalog.details.technical_criteria.main_engine.power_in_din', 'Power in DIN'),
      value:
        techCrit?.mainEngine.powerKw &&
        techCrit?.mainEngine.powerHp &&
        t('catalog.iam.vehicle.kwPower', '{{kwPower}}KW', { kwPower: techCrit?.mainEngine.powerKw }) +
          t('catalog.iam.vehicle.hpPower', '{{hpPower}}HP', { hpPower: techCrit?.mainEngine.powerHp }),
    },
    {
      title: t('catalog.details.technical_criteria.main_engine.displacement', 'Displacement'),
      value: techCrit?.mainEngine.displacement,
    },
    {
      title: t('catalog.details.technical_criteria.transmission.drive_type', 'Drive'),
      value: joinStrings(techCrit?.transmission.driveType),
    },
    {
      title: t(
        'catalog.details.technical_criteria.transmission.gearbox_type_number_of_gears',
        'Gearbox type/Number of gears',
      ),
      value:
        techCrit?.transmission.gearbox &&
        techCrit?.transmission.gearboxType &&
        t(
          'catalog.details.technical_criteria.transmission.gearbox_type_number_of_gears.value',
          '{{ gearbox }} -speed {{ gearboxType }}',
          { gearbox: techCrit?.transmission.gearbox, gearboxType: techCrit?.transmission.gearboxType },
        ),
    },
    {
      title: t('catalog.details.technical_criteria.transmission.gearbox_code', 'Gearbox code'),
      value: techCrit?.transmission.gearboxCode,
    },
    {
      title: t('catalog.details.technical_criteria.main_engine.co2', 'CO2'),
      value:
        techCrit?.mainEngine.co2 &&
        techCrit?.mainEngine.co2Norm &&
        `${techCrit?.mainEngine.co2} [${techCrit?.mainEngine.co2Norm}]`,
    },
  ].filter((item) => item.value) as TableDataType;
};
